// engine-exception: the customer portal is a separate SPA for portal principals (blueprint §9) — its own API group (/portal/v1), its own auth gate and shell; it is not a staff ScreenDef
import { useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import { EngineProvider } from '../engine/api/context';
import type { Client } from '../engine/api/client';
import { ToastProvider } from '../engine/design/primitives/Toast';
import { ConfirmProvider } from '../engine/design/primitives/Overlay';
import { createPortalClient } from './client';
import { portalSignOut, portalToken } from './auth';
import { PortalShell } from './PortalShell';
import { HomePage } from './pages/home';
import { NewTicketPage, TicketPage, TicketsPage } from './pages/tickets';
import { ContractsPage, InvoicePage, InvoicesPage, QuoteDetailPage, QuotesPage } from './pages/commerce';
import { AppointmentsPage, ApprovalPage, ApprovalsPage, ArticlePage, KbPage, ProjectPage, ProjectsPage } from './pages/work';
import { NotificationsPage, PeoplePage, ProfilePage } from './pages/people';
import { AcceptPage, ApproveLinkPage, CsatLinkPage, HaloCallbackPage, LoginPage } from './pages/auth';
import './portal.css';

// Mounted by App.tsx at /portal/*. Everything under it uses the PORTAL client (base URL suffixed
// /portal): pre-auth pages (login, accept-invite, signed CSAT / one-click approval links) and,
// behind the shell's auth gate, the customer pages. Its own QueryClient keeps portal and staff
// caches apart; its own EngineProvider means engine hooks (useResourceList…) hit /portal/v1.

export function PortalApp({ client }: { client?: Client } = {}) {
  const [qc] = useState(() => new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } }));
  const [signedOut, setSignedOut] = useState(0);
  const api = useMemo(
    () => client ?? createPortalClient(portalToken, { onUnauthorized: () => setSignedOut((n) => n + 1) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, signedOut],
  );
  const signOut = async () => {
    await portalSignOut();
    qc.clear();
    setSignedOut((n) => n + 1);
  };
  return (
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <ConfirmProvider>
          <EngineProvider client={api} modules={[]} principal={{}} signOut={signOut}>
            <Routes>
              <Route path="login" element={<LoginPage />} />
              <Route path="auth/halo" element={<HaloCallbackPage />} />
              <Route path="accept" element={<AcceptPage />} />
              <Route path="csat" element={<CsatLinkPage />} />
              <Route path="approve" element={<ApproveLinkPage />} />
              <Route element={<PortalShell onSignOut={signOut} />}>
                <Route index element={<HomePage />} />
                <Route path="tickets" element={<TicketsPage />} />
                <Route path="tickets/new" element={<NewTicketPage />} />
                <Route path="tickets/:id" element={<TicketPage />} />
                <Route path="approvals" element={<ApprovalsPage />} />
                <Route path="approvals/:id" element={<ApprovalPage />} />
                <Route path="quotes" element={<QuotesPage />} />
                <Route path="quotes/:id" element={<QuoteDetailPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="invoices/:id" element={<InvoicePage />} />
                <Route path="contracts" element={<ContractsPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="projects/:id" element={<ProjectPage />} />
                <Route path="appointments" element={<AppointmentsPage />} />
                <Route path="kb" element={<KbPage />} />
                <Route path="kb/:id" element={<ArticlePage />} />
                <Route path="contacts" element={<PeoplePage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="*" element={<Navigate to="/portal" replace />} />
              </Route>
            </Routes>
          </EngineProvider>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
